import React, { useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { Alert, Button, Checkbox, DatePicker, Form, Input, Steps } from 'antd';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import PhoneInput from '@src/components/PhoneInput';
import { DateUtil } from '@src/packages/utils/dates';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import {
  buildJurisdictionIdentifier,
  normalizeNationalId,
  resolveCountryCode,
} from '@src/packages/utils/nationalId';

import BoxContainer from 'components/BoxContainer';
import Title from 'containers/App/Auth/components/Title';

import IntlMessages from 'util/IntlMessages';

import { validator } from '../../screens/UserForm/utils/validator';
import UserAddressField from '../UserAddressField';

import styles from './styles.module.less';

const STEP = {
  PROFILE: 0,
  CONTACT: 1,
  ACCOUNT: 2,
};

const PROFILE_FIELDS = ['firstName', 'lastName', 'idCard', 'birthDate', 'email'];
const CONTACT_FIELDS = ['phone', ['address', 'address']];

/**
 * Invite acceptance wizard: personal profile + contact, then password/terms.
 * Time zone, flexible schedule, and professional email come from the organization.
 *
 * The document is the one the inviting organization operates under, so its label,
 * format, and validation come from the organization country (RUT in Chile, DNI in
 * Argentina) and not from the address the invitee types later on.
 */
const ConfirmUserForm = ({
  email,
  loading = false,
  onSign,
  firstName = '',
  lastName = '',
  idCard = '',
  birthDate = null,
  phone = '',
  address = null,
  organizationCountry = '',
  organizationName = '',
  titleId = 'users.invitation.accept.title',
  skipPassword = false,
  orgs = [],
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [step, setStep] = useState(STEP.PROFILE);
  const withoutOrg = orgs.length === 0;
  const disableInputs = loading || withoutOrg;

  const organizationCountryCode = resolveCountryCode(organizationCountry);
  const { country, countryRulesForm } = useRulesByCountry({
    countryName: organizationCountry || address?.country,
  });
  const validations = useMemo(() => validator(country, intl), [country, intl]);
  const documentLabel = countryRulesForm?.idCard?.label || 'users.personalDocument.label';
  const documentPlaceholder = countryRulesForm?.identifier?.pattern?.placeholder;

  const birthDateMs = DateUtil.getTimestampMs(birthDate);
  const initialBirthDate = birthDateMs == null ? null : moment(birthDateMs);

  const handleFinish = values => {
    const { acceptedTerms: _acceptedTerms, email: _email, ...profile } = values;
    const typedDocument = profile.idCard || '';
    onSign({
      ...profile,
      birthDate: profile.birthDate ? profile.birthDate.toDate?.() || profile.birthDate : null,
      idCard: normalizeNationalId(country, typedDocument),
      // The organization jurisdiction document is the one typed above, so it is
      // derived instead of asked twice.
      jurisdictionId:
        buildJurisdictionIdentifier(organizationCountry, typedDocument) || undefined,
    });
  };

  // Guard double-clicks: overlapping goNext calls both read the same `step` and
  // can skip Contact validation (PROFILE → ACCOUNT) under fast input / CI load.
  const isAdvancingRef = useRef(false);

  const goNext = async () => {
    if (isAdvancingRef.current) {
      return;
    }
    isAdvancingRef.current = true;
    const fields = step === STEP.PROFILE ? PROFILE_FIELDS : CONTACT_FIELDS;
    try {
      await form.validateFields(fields);
      setStep(current => current + 1);
    } catch (error) {
      // Ant Design rejects with { errorFields, outOfDate, values } instead of Error.
      // Field messages are already rendered; do not advance or leak to Sentry.
      if (error?.errorFields) {
        return;
      }
      throw error;
    } finally {
      isAdvancingRef.current = false;
    }
  };

  return (
    <BoxContainer loading={loading}>
      <Title>
        {organizationName ? (
          <IntlMessages id={titleId} values={{ name: organizationName }} />
        ) : undefined}
      </Title>
      <Steps
        className={styles.steps}
        current={step}
        size="small"
        items={[
          { title: intl.formatMessage({ id: 'users.invitation.accept.step.profile' }) },
          { title: intl.formatMessage({ id: 'users.invitation.accept.step.contact' }) },
          { title: intl.formatMessage({ id: 'users.invitation.accept.step.account' }) },
        ]}
      />
      <Form
        form={form}
        initialValues={{
          acceptedTerms: null,
          email,
          password: '',
          firstName,
          lastName,
          idCard: idCard
            ? countryRulesForm?.identifier?.formatIdentifier?.(idCard) || idCard
            : '',
          birthDate: initialBirthDate,
          phone: phone || '',
          address: {
            address: address?.address || '',
            city: address?.city || '',
            country: address?.country || organizationCountry || '',
            region: address?.region || '',
            zip: address?.zip || '',
            location: address?.location || null,
          },
        }}
        layout="vertical"
        requiredMark={false}
        name="inviteAccept"
        onFinish={handleFinish}
        className="gx-signin-form gx-form-row0"
      >
        <div className={step === STEP.PROFILE ? undefined : styles.stepHidden}>
          <Form.Item
            label={intl.formatMessage({ id: 'onboarding.stepOne.name' })}
            name="firstName"
            rules={[{ validator: validations.name }]}
          >
            <Input disabled={disableInputs} />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({ id: 'onboarding.stepOne.lastname' })}
            name="lastName"
            rules={[{ validator: validations.lastName }]}
          >
            <Input disabled={disableInputs} />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({ id: documentLabel })}
            name="idCard"
            rules={[
              { required: true, message: intl.formatMessage({ id: 'form.required.msg' }) },
              { validator: validations.idCard },
            ]}
          >
            <Input
              disabled={disableInputs}
              placeholder={documentPlaceholder}
              onKeyUp={e => {
                const value = e.currentTarget.value;
                const formatted = countryRulesForm?.identifier?.formatIdentifier?.(value) || value;
                form.setFieldValue('idCard', formatted);
              }}
            />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({ id: 'user.form.birth.date' })}
            name="birthDate"
            rules={[{ required: true, message: intl.formatMessage({ id: 'form.required.msg' }) }]}
          >
            <DatePicker
              className="gx-w-100"
              format={DateUtil.DEFAULT_DATE_FORMAT}
              disabled={disableInputs}
              disabledDate={current => current && current.isAfter(moment())}
            />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({ id: 'appModule.email' })}
            name="email"
            rules={[
              {
                type: 'email',
                message: intl.formatMessage({ id: 'form.format.msg' }),
              },
              { required: true, message: intl.formatMessage({ id: 'form.required.msg' }) },
              {
                validator: (_, value) => {
                  if (value !== email) {
                    return Promise.reject(
                      new Error(intl.formatMessage({ id: 'users.invitation.accept.email.corrupt' })),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input disabled readOnly autoComplete="username" />
          </Form.Item>
        </div>

        <div className={step === STEP.CONTACT ? undefined : styles.stepHidden}>
          <Form.Item
            label={intl.formatMessage({ id: 'user.form.phone' })}
            name="phone"
            rules={[
              { required: true, message: intl.formatMessage({ id: 'form.required' }) },
              { validator: validations.phone },
            ]}
          >
            <PhoneInput countryCode={organizationCountryCode || undefined} disabled={disableInputs} />
          </Form.Item>

          <UserAddressField form={form} disabled={disableInputs} required />
        </div>

        <div className={step === STEP.ACCOUNT ? undefined : styles.stepHidden}>
          <Form.Item
            hidden={skipPassword}
            label={intl.formatMessage({ id: 'appModule.password' })}
            name="password"
            rules={[
              {
                required: !skipPassword,
                message: intl.formatMessage({ id: 'form.required.msg' }),
              },
              {
                min: 6,
                message: intl.formatMessage({ id: 'form.minCharLength' }, { length: 6 }),
              },
            ]}
          >
            <Input.Password autoComplete="new-password" disabled={disableInputs} />
          </Form.Item>
          <div className={styles.subpanel}>
            <Form.Item
              name="acceptedTerms"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error(intl.formatMessage({ id: 'form.required.msg' }))),
                },
              ]}
            >
              <Checkbox disabled={disableInputs}>
                <IntlMessages id="auth.signUp.iAccept" />{' '}
                <a
                  className="gx-login-form-forgot"
                  href="https://www.operonda.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IntlMessages id="auth.signUp.termsAndConditions" />
                </a>
              </Checkbox>
            </Form.Item>
          </div>
        </div>

        {withoutOrg && (
          <Alert
            type="error"
            showIcon
            message={intl.formatMessage({ id: 'users.invitation.accept.withoutOrg' })}
            className="gx-mt-4"
          />
        )}

        <div className={styles.actions}>
          {step > STEP.PROFILE ? (
            <Button disabled={disableInputs} onClick={() => setStep(current => current - 1)}>
              <IntlMessages id="button.goBack" />
            </Button>
          ) : null}
          {step < STEP.ACCOUNT ? (
            <Button
              type="primary"
              className={classnames('gx-mb-0', styles.submitButton)}
              disabled={disableInputs}
              onClick={goNext}
            >
              <IntlMessages id="button.continue" />
            </Button>
          ) : (
            <Button
              type="primary"
              className={classnames('gx-mb-0', styles.submitButton)}
              htmlType="submit"
              loading={disableInputs && !withoutOrg}
              disabled={disableInputs}
            >
              <IntlMessages id="users.invitation.accept.sign" />
            </Button>
          )}
        </div>
      </Form>
    </BoxContainer>
  );
};

ConfirmUserForm.propTypes = {
  email: PropTypes.string.isRequired,
  onSign: PropTypes.func.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  idCard: PropTypes.string,
  birthDate: PropTypes.any,
  phone: PropTypes.string,
  address: PropTypes.object,
  organizationCountry: PropTypes.string,
  loading: PropTypes.bool,
  organizationName: PropTypes.string,
  titleId: PropTypes.string,
  skipPassword: PropTypes.bool,
  orgs: PropTypes.array.isRequired,
};

export default React.memo(ConfirmUserForm);
